// /approvals: what waits on Phoneware. Orders to bill (#1621): Billing ops and Super
// admins choose the OneBill account each signed order bills to. Below-floor quotes:
// they approve or send back with a note. Sales and Support at Phoneware see the same
// queue, view only (mock M1). An agent dealer sees its own quotes in the queue; a
// wholesale dealer quotes its own prices and has nothing here.
import React, { useState } from "react";
import { formatCents, formatTerm, quoteTotals, type AwaitingBillingApprovalOrder, type CustomerSummary, type Dealer, type Order, type Quote } from "@phoneware/lens-domain";
import { companyTypeName } from "../api-customers.js";
import { Icon } from "../icons.js";
import { Link, navigate, rowLink } from "../router.js";
import { useLoad, useSession } from "../session.js";
import { errorText, plural, when, whenIn } from "../format.js";
import { Callout, Chip, DecidedBy, Head, ViewOnly, useTitle, useToast } from "../ui.js";
import { useRepNames } from "./Quotes.js";
import { BillingApprovalSheet, DecisionChip, useDecision } from "./BillingApproval.js";
import { useBillingChanges } from "../billing-changes.js";

/**
 * One order to bill in the queue table. For whoever approves billing, clicking anywhere on it, or Review, opens the approval
 * sheet; for a tier that only watches, View opens the order.
 */
function BillTableRow({
  order,
  dealerName,
  companyType,
  approves,
  onOpen,
}: {
  order: AwaitingBillingApprovalOrder;
  dealerName: string;
  companyType: number | null;
  approves: boolean;
  onOpen: () => void;
}): React.ReactElement {
  const decision = useDecision(order);
  return (
    <tr data-testid={`approvals-bill-${order.id}`} onClick={(e) => (e.target instanceof Element && e.target.closest("a, button") ? undefined : onOpen())}>
      <td className="mono hide-sm">{order.number}</td>
      <td>
        <b>{order.customerName}</b>
        <span className="sub">
          {dealerName ? `${dealerName} \u00b7 ` : ""}
          <span className="nw">{order.number}</span>
          {companyType === null ? "" : ` \u00b7 ${companyTypeName(companyType)}`}
        </span>
      </td>
      <td className="num">
        {formatCents(order.monthly ?? 0)}
        <span className="sub">{formatCents(order.oneTime ?? 0)} once</span>
      </td>
      <td>
        <DecisionChip decision={decision} testId={`approvals-decision-${order.id}`} />
        <span className="sub">{decision.text}</span>
      </td>
      <td className="muted hide-sm">{whenIn(order.billingApproval?.waitingSince ?? order.updatedAt)}</td>
      <td className="num hide-sm">
        {approves ? (
          <button className="btn small" type="button" onClick={onOpen}>
            Review
          </button>
        ) : (
          <button className="btn small white" type="button" data-testid={`approvals-view-${order.id}`} onClick={onOpen}>
            View
          </button>
        )}
      </td>
    </tr>
  );
}

function AskCard({ quote, dealer, ops, decides, onDecided }: { quote: Quote; dealer: Dealer | null; ops: boolean; decides: boolean; onDecided: (q: Quote) => void }): React.ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const repName = useRepNames();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const totals = quoteTotals(quote.lines, dealer?.model ?? "agent");
  let gapMonthly = 0;
  let gapOnce = 0;
  for (const r of quote.approval.reasons) {
    const line = quote.lines.find((l) => l.productId === r.productId);
    const gap = (r.floorPrice - r.unitPrice) * (line?.qty ?? 1);
    if (line?.billing === "one_time") gapOnce += gap;
    else gapMonthly += gap;
  }
  const gapText = [gapMonthly ? `${formatCents(gapMonthly)} a month` : "", gapOnce ? `${formatCents(gapOnce)} one-time` : ""].filter(Boolean).join(" and ");

  async function decide(decision: "approved" | "sent_back"): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const next = await api.decideApproval(quote.id, { decision, note: note.trim() || null });
      toast(`${next.number} ${decision === "approved" ? "approved" : "sent back"}. ${repName(next.repEmail)} sees it now.`);
      onDecided(next);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <article className="card ask">
      <div className="card-h ask-h">
        <div>
          <h2>
            <Link to={`/quotes/${encodeURIComponent(quote.id)}`}>{quote.customerName}</Link> <span className="lite muted">· {quote.number}</span>
          </h2>
          <span className="row-m">
            {[dealer?.name, repName(quote.repEmail), formatTerm(quote.term), `asked ${whenIn(quote.updatedAt)}`].filter(Boolean).join(" \u00b7 ")}
          </span>
        </div>
        <Chip kind="warn" icon="triangle-alert">
          Below the floor
        </Chip>
      </div>
      <div className="ask-body">
        <div className="ask-what">
          <div className="tablewrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="num">Qty</th>
                  <th className="num">List</th>
                  <th className="num">Floor</th>
                  <th className="num">Asking</th>
                </tr>
              </thead>
              <tbody>
                {quote.approval.reasons.map((r) => {
                  const line = quote.lines.find((l) => l.productId === r.productId);
                  return (
                    <tr key={r.productId}>
                      <td>{r.name}</td>
                      <td className="num">{line?.qty ?? ""}</td>
                      <td className="num">{line ? formatCents(line.listPrice) : ""}</td>
                      <td className="num">{formatCents(r.floorPrice)}</td>
                      <td className="num">
                        <b className="g-low-t">{formatCents(r.unitPrice)}</b>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="ask-reason">
            <b>Why:</b> {quote.approval.requestNote || "No reason given yet."}
          </p>
          <p className="card-note">
            {gapText ? `${gapText} under the floor. ` : ""}The whole quote is {formatCents(totals.monthly)} a month and {formatCents(totals.oneTime)} one-time.
          </p>
        </div>
        {ops && decides ? (
          <div className="ask-do">
            <label className="field">
              <span>Note back</span>
              <textarea className="input" placeholder="Optional. They see this on the quote." value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <div className="btnrow">
              <button className="btn primary" type="button" disabled={busy} onClick={() => void decide("approved")}>
                <Icon name="check" size={15} />
                Approve
              </button>
              <button className="btn" type="button" disabled={busy} onClick={() => void decide("sent_back")}>
                <Icon name="undo-2" size={15} />
                Send back
              </button>
            </div>
            {error ? (
              <Callout kind="stop" icon="triangle-alert">
                {error}
              </Callout>
            ) : null}
          </div>
        ) : ops ? (
          <div className="ask-do">
            <DecidedBy testId={`approvals-readonly-${quote.id}`}>Billing ops or a Super admin decides this one.</DecidedBy>
          </div>
        ) : (
          <div className="ask-do">
            <p className="card-p">Phoneware decides. You will see it on Today.</p>
          </div>
        )}
      </div>
    </article>
  );
}

export function Approvals(): React.ReactElement {
  const { me, api, can, everyDealer } = useSession();
  const ops = everyDealer;
  // Billing ops and Super admins decide; Sales and Support at Phoneware watch the same queue.
  const decides = can("approvals.decide");
  const approves = can("billing.approve");
  useTitle("Approvals");
  const wholesale = me.dealer?.model === "wholesale";
  const waiting = useLoad<Quote[]>(() => (wholesale ? Promise.resolve([]) : ops ? api.approvals() : api.quotes("needs_approval")), [ops, wholesale]);
  const decided = useLoad<Quote[]>(() => (wholesale ? Promise.resolve([]) : api.quotes()), [wholesale]);
  const dealers = useLoad<Dealer[]>(() => api.dealers(), []);
  const repName = useRepNames();
  const changes = useBillingChanges();
  const toBill = useLoad<AwaitingBillingApprovalOrder[]>(() => (ops ? api.ordersToBill() : Promise.resolve([])), [ops, changes]);
  const orders = useLoad<Order[]>(() => (ops ? api.orders() : Promise.resolve([])), [ops, changes]);
  const customers = useLoad<CustomerSummary[]>(() => (ops ? api.customers() : Promise.resolve([])), [ops]);
  const [approving, setApproving] = useState<string | null>(null);

  if (wholesale) {
    return (
      <>
        <Head title="Approvals" sub={`${me.dealer?.name ?? "Your dealer"} is a reseller.`} />
        <section className="card">
          <Callout icon="info">
            A reseller buys at a flat cost and marks up; Phoneware still bills both. You quote your own prices, so none of your quotes wait on Phoneware. Omni only warns you when a line sells below what it costs you.{" "}
            <Link to="/catalog">Your buy prices</Link>
          </Callout>
        </section>
      </>
    );
  }

  const list = waiting.data ?? [];
  const bill = [...(toBill.data ?? [])].sort((a, b) => (a.billingApproval?.waitingSince ?? a.updatedAt).localeCompare(b.billingApproval?.waitingSince ?? b.updatedAt));
  const today = new Date().toDateString();
  // Billing decisions ops made today; orders from before billing approval existed have no one who decided.
  const billedToday = (orders.data ?? [])
    .filter((o) => o.billingApproval?.state === "approved" && o.billingApproval.decidedBy && o.billingApproval.decidedAt && new Date(o.billingApproval.decidedAt).toDateString() === today)
    .sort((a, b) => (b.billingApproval?.decidedAt ?? "").localeCompare(a.billingApproval?.decidedAt ?? ""));
  const dealerName = (id: string) => dealers.data?.find((d) => d.id === id)?.name ?? "";
  const history = (decided.data ?? [])
    .filter((q) => q.approval.decision !== null && q.approval.decidedAt !== null)
    .sort((a, b) => (b.approval.decidedAt ?? "").localeCompare(a.approval.decidedAt ?? ""))
    .slice(0, 12);

  return (
    <>
      <Head
        title="Approvals"
        sub={
          ops
            ? `${plural(bill.length, "order")} to bill and ${plural(list.length, "quote")} below the floor. OneBill bills nothing and Autotask keeps the company as it is until ${decides || approves ? "you approve" : "someone approves"}.`
            : "Your quotes priced below the floor, waiting on Phoneware."
        }
      />
      {ops && !decides && !approves ? <ViewOnly testId="approvals-view-only">Billing ops and Super admins decide approvals. You can see the whole queue.</ViewOnly> : null}
      {ops ? (
        <section className="card" data-testid="approvals-bill">
          <div className="card-h">
            <h2>Orders to bill</h2>
            {bill.length ? (
              <Chip kind="warn" icon="receipt">
                {plural(bill.length, "order")}
              </Chip>
            ) : null}
            <span className="note">Oldest first</span>
          </div>
          {toBill.error ? (
            <Callout kind="stop" icon="triangle-alert">
              {toBill.error.message}
            </Callout>
          ) : !toBill.data ? (
            <div className="empty">Loading orders to bill.</div>
          ) : bill.length === 0 ? (
            <div className="empty">No orders waiting to bill.</div>
          ) : (
            <div className="tablewrap">
              <table className="t t-bill-q">
                <thead>
                  <tr>
                    <th className="hide-sm">Order</th>
                    <th>Customer</th>
                    <th className="num">Monthly</th>
                    <th>OneBill account</th>
                    <th className="hide-sm">Waiting since</th>
                    <th className="hide-sm">
                      <span className="sr">Review</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {bill.map((o) => (
                    <BillTableRow
                      key={o.id}
                      order={o}
                      dealerName={dealerName(o.dealerId)}
                      companyType={customers.data?.find((c) => c.autotaskCompanyId === o.customerId)?.companyType ?? null}
                      approves={approves}
                      onOpen={() => (approves ? setApproving(o.id) : navigate(`/orders/${encodeURIComponent(o.id)}`))}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}
      {waiting.error ? (
        <Callout kind="stop" icon="triangle-alert">
          {waiting.error.message}
        </Callout>
      ) : !waiting.data ? (
        <section className="card">
          <div className="empty">Loading the queue.</div>
        </section>
      ) : list.length === 0 ? (
        <section className="card">
          <div className="empty">{ops ? "No quotes below the floor." : "Nothing of yours is waiting on Phoneware."}</div>
        </section>
      ) : (
        <div className="stack">
          {list.map((q) => (
            <AskCard
              key={q.id}
              quote={q}
              dealer={dealers.data?.find((d) => d.id === q.dealerId) ?? me.dealer}
              ops={ops}
              decides={decides}
              onDecided={() => {
                waiting.set(list.filter((x) => x.id !== q.id));
                decided.reload();
              }}
            />
          ))}
        </div>
      )}
      {billedToday.length ? (
        <section className="card decided" data-testid="approvals-billed-today">
          <div className="card-h">
            <h2>Billing decided today</h2>
          </div>
          <div className="tablewrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Decision</th>
                  <th className="hide-sm">By</th>
                </tr>
              </thead>
              <tbody>
                {billedToday.map((o) => {
                  const a = o.billingApproval;
                  return (
                    <tr key={o.id} {...rowLink(`/orders/${encodeURIComponent(o.id)}`)}>
                      <td className="mono">
                        <Link to={`/orders/${encodeURIComponent(o.id)}`}>{o.number}</Link>
                      </td>
                      <td>
                        <b>{o.customerName}</b>
                      </td>
                      <td>
                        <Chip kind="ok" icon="check">
                          Approved
                        </Chip>
                        <span className="sub">
                          {a?.account?.kind === "create" ? (
                            "New OneBill account"
                          ) : a?.account?.kind === "existing" ? (
                            <>
                              On <span className="nw">{a.account.accountNumber}</span>
                            </>
                          ) : null}
                        </span>
                      </td>
                      <td className="muted hide-sm">
                        {a?.decidedBy ? repName(a.decidedBy) : ""}
                        {a?.decidedAt ? `, ${when(a.decidedAt)}` : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      {history.length ? (
        <section className="card decided">
          <div className="card-h">
            <h2>Decided</h2>
          </div>
          <div className="tablewrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Quote</th>
                  <th>Customer</th>
                  <th>Decision</th>
                  <th className="hide-sm">By</th>
                </tr>
              </thead>
              <tbody>
                {history.map((q) => (
                  <tr key={q.id} {...rowLink(`/quotes/${encodeURIComponent(q.id)}`)}>
                    <td className="mono">
                      <Link to={`/quotes/${encodeURIComponent(q.id)}`}>{q.number}</Link>
                    </td>
                    <td>
                      <b>{q.customerName}</b>
                    </td>
                    <td>
                      {q.approval.decision === "approved" ? (
                        <Chip kind="ok" icon="check">
                          Approved
                        </Chip>
                      ) : (
                        <Chip kind="warn" icon="undo-2">
                          Sent back
                        </Chip>
                      )}
                    </td>
                    <td className="muted hide-sm">
                      {q.approval.decidedBy ? repName(q.approval.decidedBy) : ""}
                      {q.approval.decidedAt ? `, ${when(q.approval.decidedAt)}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      {approving ? (
        <BillingApprovalSheet
          orderId={approving}
          onClose={() => {
            setApproving(null);
            toBill.reload();
            orders.reload();
          }}
        />
      ) : null}
    </>
  );
}
